/**
 * Workshop view — Session 4 rework.
 *
 * Adds two new panels:
 *   - <ForgeCard>: Rarity → Category → Base item, with server-authoritative
 *     preview (cost + budget + affix count) and optional lock count.
 *   - <EnchantCard>: server-authoritative enchant with per-stat lock toggles
 *     and 55/25/15/5 outcome probabilities preview.
 *
 * Fusion / Dismantle / Class-Change Scroll / Set preview are unchanged from
 * the previous WorkshopView.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Wand2, Flame, Scroll, Sword, Shield, Sparkles, BookOpen, Recycle,
  Hammer, Lock, Unlock, ChevronRight,
} from "lucide-react";
import { ElementBadge } from "../ElementBadge";
import { normalizeElement } from "../../constants/elements";
import T from "@/components/T";

const CLASSES = [
  { name: "Warrior",  stat: "strength",     Icon: Sword },
  { name: "Guardian", stat: "defense",      Icon: Shield },
  { name: "Rogue",    stat: "dexterity",    Icon: Sparkles },
  { name: "Wizard",   stat: "intelligence", Icon: BookOpen },
  { name: "Bard",     stat: "charisma",     Icon: Wand2 },
];

const RARITY_ORDER = ["common", "uncommon", "rare", "epic", "legendary", "mythic"];
const RARITY_COLOR = {
  common: "#8c8c8c", uncommon: "#6E8B3D", rare: "#4A7BC7",
  epic: "#A97AB0", legendary: "#E5C34A", mythic: "#E33333",
};
const CATEGORIES = [
  { id: "weapon",  label: "Weapons", Icon: Sword },
  { id: "armor",   label: "Armor",   Icon: Shield },
  { id: "trinket", label: "Trinkets", Icon: Sparkles },
];


// ── Set preview (unchanged from previous) ─────────────────────────

function SetPreview({ ch }) {
  const equipped = ch.equipped || {};
  return (
    <div className="panel p-3" data-testid="set-preview">
      <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] mb-2"><T>Class Sets</T></div>
      <div className="space-y-1">
        {CLASSES.map(c => {
          const worn = ["weapon", "armor", "trinket"].filter(s => equipped[s]?.preferred_class === c.name).length;
          const complete = worn === 3;
          const Icon = c.Icon;
          return (
            <div key={c.name} className={`panel-inner p-2 flex items-center gap-2 ${complete ? "border-[#B89C3A]" : ""}`}>
              <Icon className="w-4 h-4 shrink-0" style={{ color: complete ? "#E5C34A" : "#8c8c8c" }} strokeWidth={1.5} />
              <div className="flex-1 min-w-0">
                <div className={`font-heading text-xs ${complete ? "text-[#E5C34A]" : "text-[#E2DFD8]"}`}>{c.name} Set</div>
                <div className="font-mono text-[10px] text-[#8c8c8c]">+3 {c.stat} when weapon+armor+trinket match</div>
              </div>
              <div className="font-mono text-xs text-[#B89C3A]">{worn}/3</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ── NEW: Forge card (Rarity → Category → Item + preview) ─────────

function ForgeCard({ ch, setCh }) {
  const [rarity, setRarity] = useState(null);
  const [category, setCategory] = useState(null);
  const [baseId, setBaseId] = useState(null);
  const [catalog, setCatalog] = useState({ bases: [], budgets: {} });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.forgeCatalog().then(setCatalog).catch(() => {}); }, []);

  useEffect(() => {
    if (!rarity || !baseId) { setPreview(null); return; }
    let dead = false;
    api.forgePreview(rarity, baseId, 0)
      .then(p => { if (!dead) setPreview(p); })
      .catch(() => { if (!dead) setPreview(null); });
    return () => { dead = true; };
  }, [rarity, baseId]);

  const bases = (catalog.bases || []).filter(b => !category || b.category === category);
  const materials = (ch.inventory || []).filter(i => i.slot === "material").length;
  const canAfford = preview && materials >= preview.cost.materials && (ch.gold || 0) >= preview.cost.gold;

  const doForge = async () => {
    if (!rarity || !baseId || busy) return;
    setBusy(true);
    try {
      const res = await api.forge(rarity, baseId, 0);
      setCh(res.character);
      toast.success(`Forged ${res.item.rarity} ${res.item.name}`);
      setRarity(null); setCategory(null); setBaseId(null); setPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Forge failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="panel p-3 space-y-3" data-testid="forge-card">
      <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] flex items-center gap-2">
        <Hammer className="w-4 h-4 text-[#B89C3A]" strokeWidth={1.5} /> <T>Forge</T>
      </div>
      <p className="italic text-[10px] text-[#666]">
        <T>Choose a rarity, then a category, then a base. Preview cost & budget before you commit.</T>
      </p>

      {/* Step 1: Rarity */}
      <div>
        <div className="font-mono text-[9px] tracking-widest uppercase text-[#666] mb-1">1 · Rarity</div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-1" data-testid="forge-rarities">
          {RARITY_ORDER.map(r => (
            <button key={r} data-testid={`forge-rarity-${r}`} onClick={() => setRarity(r)}
              style={{ borderColor: rarity === r ? RARITY_COLOR[r] : "#262626",
                       color: rarity === r ? RARITY_COLOR[r] : "#8c8c8c" }}
              className="border font-heading text-[9px] tracking-widest uppercase py-1.5 transition-colors">
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* Step 2: Category */}
      {rarity && (
        <div>
          <div className="font-mono text-[9px] tracking-widest uppercase text-[#666] mb-1">2 · Category</div>
          <div className="grid grid-cols-3 gap-1" data-testid="forge-categories">
            {CATEGORIES.map(c => {
              const active = category === c.id;
              const Icon = c.Icon;
              return (
                <button key={c.id} data-testid={`forge-cat-${c.id}`} onClick={() => { setCategory(c.id); setBaseId(null); }}
                  className={`border py-1.5 flex items-center justify-center gap-1 font-heading text-[9px] tracking-widest uppercase transition-colors
                    ${active ? "border-[#B89C3A] text-[#E5C34A]" : "border-[#262626] text-[#8c8c8c]"}`}>
                  <Icon size={12} strokeWidth={1.6} /> {c.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 3: Base item — only shown when rarity+category picked */}
      {rarity && category && (
        <div>
          <div className="font-mono text-[9px] tracking-widest uppercase text-[#666] mb-1">3 · Base</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-48 overflow-y-auto" data-testid="forge-bases">
            {bases.map(b => (
              <button key={b.id} data-testid={`forge-base-${b.id}`} onClick={() => setBaseId(b.id)}
                className={`border p-2 text-left transition-colors
                  ${baseId === b.id ? "border-[#B89C3A] bg-[#B89C3A]/10" : "border-[#262626] hover:border-[#4A7BC7]"}`}>
                <div className="font-heading text-xs text-[#E2DFD8]">{b.name}</div>
                <div className="font-mono text-[9px] text-[#8c8c8c]">value {b.base_value}g · {b.stat || "any stat"}</div>
              </button>
            ))}
            {bases.length === 0 && <div className="col-span-full italic text-[10px] text-[#666] py-3 text-center">No bases in this category yet.</div>}
          </div>
        </div>
      )}

      {/* Preview + confirm */}
      {preview && (
        <div className="panel-inner p-2 border-l-4" style={{ borderLeftColor: RARITY_COLOR[preview.rarity] }} data-testid="forge-preview">
          <div className="font-heading text-[10px] tracking-widest uppercase mb-1"
               style={{ color: RARITY_COLOR[preview.rarity] }}>
            Preview · {preview.rarity} {preview.base.name}
          </div>
          <div className="font-mono text-[10px] text-[#B0ADA5] space-y-0.5">
            <div>Power budget: <span className="text-[#E5C34A]">{preview.power_budget}</span></div>
            <div>Affixes rolled: <span className="text-[#E5C34A]">{preview.possible_affixes}</span> from [{preview.stat_pool.join(", ")}]</div>
            <div>Cost: <span className={materials >= preview.cost.materials ? "text-[#6E8B3D]" : "text-[#E33333]"}>{preview.cost.materials} materials</span> · <span className={ch.gold >= preview.cost.gold ? "text-[#6E8B3D]" : "text-[#E33333]"}>{preview.cost.gold}g</span></div>
          </div>
        </div>
      )}

      <button data-testid="forge-btn" onClick={doForge} disabled={busy || !canAfford}
        className="w-full py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-xs tracking-widest uppercase disabled:opacity-30">
        Forge
      </button>
    </div>
  );
}


// ── NEW: Enchant card (with lock system + probability preview) ────

const ALL_STATS = ["strength", "defense", "dexterity", "intelligence", "charisma", "concentration"];

function EnchantCard({ ch, setCh }) {
  const [targetId, setTargetId] = useState("");
  const [locked, setLocked] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const equipped = ch.equipped || {};
  const pool = [
    ...Object.values(equipped).filter(Boolean),
    ...(ch.inventory || []).filter(i => ["weapon", "armor", "trinket"].includes(i.slot)),
  ];
  const target = pool.find(i => i.id === targetId);
  const materials = (ch.inventory || []).filter(i => i.slot === "material").length;

  useEffect(() => {
    if (!targetId) { setPreview(null); return; }
    let dead = false;
    api.enchantPreview(targetId, locked)
      .then(p => { if (!dead) setPreview(p); })
      .catch(() => { if (!dead) setPreview(null); });
    return () => { dead = true; };
  }, [targetId, locked]);

  const toggleLock = (stat) => {
    setLocked(l => l.includes(stat) ? l.filter(s => s !== stat) : [...l, stat]);
  };

  const doEnchant = async () => {
    if (!targetId || busy) return;
    setBusy(true);
    try {
      const res = await api.enchant(targetId, locked);
      setCh(res.character);
      const outcome = res.outcome || {};
      const added = outcome.stats_added ? Object.entries(outcome.stats_added).map(([s, v]) => `+${v} ${s}`).join(", ") : "";
      const el = outcome.element ? ` + ${outcome.element_kind} ${outcome.element}` : "";
      toast.success(`Enchant ${outcome.kind}: ${added}${el}`);
      setLocked([]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Enchant failed"); }
    finally { setBusy(false); }
  };

  const currentBonuses = target?.stat_bonuses || (target?.stat ? { [target.stat]: target.stat_bonus || 0 } : {});
  const canAfford = preview && materials >= preview.cost.materials && (ch.gold || 0) >= preview.cost.gold;

  return (
    <div className="panel p-3 space-y-3" data-testid="enchant-card">
      <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] flex items-center gap-2">
        <Wand2 className="w-4 h-4 text-[#4A7BC7]" strokeWidth={1.5} /> Enchanter · Server RNG
      </div>
      <p className="italic text-[10px] text-[#666]">
        55% +1 stat · 25% +2 stats · 15% +3 stats · 5% +3 stats + elemental affix. Never downgrades. Lock stats to preserve exact value (higher cost).
      </p>

      <select data-testid="enchant-target" value={targetId} onChange={e => { setTargetId(e.target.value); setLocked([]); }}
        className="w-full bg-[#0a0a0a] border border-[#262626] px-2 py-2 text-xs text-[#E2DFD8]">
        <option value="">— pick an equippable —</option>
        {pool.map(i => (
          <option key={i.id} value={i.id}>{i.name} ({i.slot}/{i.rarity})</option>
        ))}
      </select>

      {target && (
        <div className="panel-inner p-2 space-y-2">
          <div className="font-heading text-xs" style={{ color: RARITY_COLOR[target.rarity] }}>{target.name}</div>
          {Object.keys(currentBonuses).length > 0 && (
            <div className="space-y-1">
              <div className="font-mono text-[9px] tracking-widest uppercase text-[#666]">Stats · lock to preserve</div>
              <div className="flex flex-wrap gap-1">
                {Object.entries(currentBonuses).map(([stat, val]) => {
                  const isLocked = locked.includes(stat);
                  return (
                    <button key={stat} onClick={() => toggleLock(stat)}
                      data-testid={`enchant-lock-${stat}`}
                      className={`flex items-center gap-1 border px-2 py-1 font-mono text-[9px] uppercase tracking-widest transition-colors
                        ${isLocked ? "border-[#E5C34A] text-[#E5C34A] bg-[#B89C3A]/10" : "border-[#333] text-[#8c8c8c] hover:text-[#B0ADA5]"}`}>
                      {isLocked ? <Lock size={9} /> : <Unlock size={9} />}
                      +{val} {stat.slice(0, 3).toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {(target.elemental_damage && Object.keys(target.elemental_damage).length > 0) && (
            <div className="flex flex-wrap gap-1">
              {Object.entries(target.elemental_damage).map(([el, val]) => (
                <ElementBadge key={el} element={el} value={`+${val}`} size="sm" />
              ))}
            </div>
          )}
          {(target.elemental_resistance && Object.keys(target.elemental_resistance).length > 0) && (
            <div className="flex flex-wrap gap-1">
              {Object.entries(target.elemental_resistance).map(([el, val]) => (
                <ElementBadge key={el} element={el} value={`${Math.round(val * 100)}% res`} size="sm" />
              ))}
            </div>
          )}
        </div>
      )}

      {preview && (
        <div className="panel-inner p-2 space-y-1" data-testid="enchant-preview">
          <div className="font-heading text-[10px] tracking-widest uppercase text-[#B89C3A]">Outcomes</div>
          {preview.outcomes.map(o => (
            <div key={o.kind} className="flex items-center gap-2 font-mono text-[10px]">
              <span className="text-[#E5C34A] w-10">{Math.round(o.chance * 100)}%</span>
              <span className="text-[#B0ADA5] flex-1">
                +{o.stats} stat{o.stats > 1 ? "s" : ""}{o.elemental ? " + elemental affix" : ""}
              </span>
            </div>
          ))}
          <div className="pt-1 mt-1 border-t border-[#262626] font-mono text-[10px]">
            Cost: <span className={materials >= preview.cost.materials ? "text-[#6E8B3D]" : "text-[#E33333]"}>{preview.cost.materials} mats</span> · <span className={ch.gold >= preview.cost.gold ? "text-[#6E8B3D]" : "text-[#E33333]"}>{preview.cost.gold}g</span>
            {locked.length > 0 && <span className="text-[#B89C3A] ml-2">· {locked.length} locked (×{(1.6 ** locked.length).toFixed(2)})</span>}
          </div>
        </div>
      )}

      <button data-testid="enchant-btn" onClick={doEnchant} disabled={busy || !canAfford}
        className="w-full py-2 border border-[#4A7BC7] text-[#4A7BC7] font-heading text-xs tracking-widest uppercase disabled:opacity-30">
        Enchant
      </button>
    </div>
  );
}


// ── Unchanged: Fusion / Dismantle / Scroll ────────────────────────

function FusionCard({ ch, setCh }) {
  const [a, setA] = useState(""); const [b, setB] = useState("");
  const [preview, setPreview] = useState(null); const [busy, setBusy] = useState(false);
  const equippables = (ch.inventory || []).filter(i => ["weapon", "armor", "trinket"].includes(i.slot));
  useEffect(() => {
    if (!a || !b) { setPreview(null); return; }
    let cancelled = false;
    api.fusePreview(a, b).then(p => { if (!cancelled) setPreview(p); }).catch(() => { if (!cancelled) setPreview(null); });
    return () => { cancelled = true; };
  }, [a, b]);
  const fuse = async () => {
    if (!a || !b || busy) return;
    setBusy(true);
    try { const res = await api.fuse(a, b); setCh(res.character); setA(""); setB(""); setPreview(null); toast.success(`Fused into ${res.item.name} (${res.item.rarity})`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="panel p-3 space-y-2" data-testid="fusion-card">
      <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] flex items-center gap-2">
        <Flame className="w-4 h-4 text-[#E55A00]" strokeWidth={1.5} /> Fusion Forge
      </div>
      <p className="italic text-[10px] text-[#666]">Fuse two equippables of the same slot AND same rarity into a stronger one (rarity +1).</p>
      <select data-testid="fuse-a" value={a} onChange={e => setA(e.target.value)} className="w-full bg-[#0a0a0a] border border-[#262626] px-2 py-2 text-xs text-[#E2DFD8]">
        <option value="">— first item —</option>
        {equippables.map(i => <option key={i.id} value={i.id}>{i.name} ({i.slot}/{i.rarity})</option>)}
      </select>
      <select data-testid="fuse-b" value={b} onChange={e => setB(e.target.value)} className="w-full bg-[#0a0a0a] border border-[#262626] px-2 py-2 text-xs text-[#E2DFD8]">
        <option value="">— second item —</option>
        {equippables.filter(i => i.id !== a).map(i => <option key={i.id} value={i.id}>{i.name} ({i.slot}/{i.rarity})</option>)}
      </select>
      {preview && (
        <div className="panel-inner p-2" data-testid="fusion-preview">
          <div className="font-heading text-[10px] tracking-widest uppercase text-[#B89C3A] mb-1">Preview</div>
          <div className={`font-heading text-sm rarity-${preview.rarity}`}>{preview.name}</div>
          <div className="font-mono text-[10px] text-[#8c8c8c] mt-1">{preview.rarity} · {preview.slot} · {preview.value}g{preview.stat && ` · +${preview.stat_bonus} ${preview.stat}`}</div>
        </div>
      )}
      <button data-testid="fuse-btn" onClick={fuse} disabled={busy || !a || !b} className="w-full py-2 border border-[#E55A00] text-[#E55A00] font-heading text-xs tracking-widest uppercase disabled:opacity-30">Fuse</button>
    </div>
  );
}

function DismantleCard({ ch, setCh }) {
  const [target, setTarget] = useState(""); const [busy, setBusy] = useState(false);
  const equipment = (ch.inventory || []).filter(i => ["weapon", "armor", "trinket"].includes(i.slot));
  const selected = equipment.find(i => i.id === target);
  const yieldMap = { common: 1, uncommon: 2, rare: 3, epic: 5, legendary: 8, mythic: 12 };
  const qty = selected ? (yieldMap[selected.rarity] || 1) : 0;
  const dismantle = async () => {
    if (!target || busy) return; setBusy(true);
    try { const res = await api.dismantleItem(target); setCh(res.character); setTarget(""); toast.success(`Recovered ${res.quantity} materials`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="panel p-3 space-y-2" data-testid="dismantle-card">
      <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] flex items-center gap-2">
        <Recycle className="w-4 h-4 text-[#6E8B3D]" strokeWidth={1.5} /> Dismantle
      </div>
      <p className="italic text-[10px] text-[#666]">Break down equipment into recovered materials. Yield scales with rarity.</p>
      <select data-testid="dismantle-target" value={target} onChange={e => setTarget(e.target.value)} className="w-full bg-[#0a0a0a] border border-[#262626] px-2 py-2 text-xs text-[#E2DFD8]">
        <option value="">— pick equipment —</option>
        {equipment.map(i => <option key={i.id} value={i.id}>{i.name} ({i.slot}/{i.rarity})</option>)}
      </select>
      {selected && <div className="font-mono text-[10px] text-[#B89C3A]">Recovery: {qty} material{qty !== 1 ? "s" : ""}</div>}
      <button data-testid="dismantle-btn" onClick={dismantle} disabled={busy || !target} className="w-full py-2 border border-[#6E8B3D] text-[#6E8B3D] font-heading text-xs tracking-widest uppercase disabled:opacity-30">Dismantle</button>
    </div>
  );
}

function ScrollCard({ ch, setCh }) {
  const [busy, setBusy] = useState(false); const [confirm, setConfirm] = useState(false);
  const reset = async () => {
    setBusy(true);
    try { setCh(await api.resetStats()); toast.success("Stats reset. Reallocate as you wish in the Hero tab."); setConfirm(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="panel p-3 space-y-2" data-testid="scroll-card">
      <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] flex items-center gap-2">
        <Scroll className="w-4 h-4 text-[#E5C34A]" strokeWidth={1.5} /> Class Change Scroll
      </div>
      <p className="italic text-[10px] text-[#666]">Consume 5000g to reset all 5 stats to 5 and reclaim the points spent above the base.</p>
      {!confirm ? (
        <button data-testid="scroll-btn" onClick={() => setConfirm(true)} disabled={busy || ch.gold < 5000} className="w-full py-2 border border-[#E5C34A] text-[#E5C34A] font-heading text-xs tracking-widest uppercase disabled:opacity-30">Use Scroll (5000g)</button>
      ) : (
        <div className="flex gap-1">
          <button data-testid="scroll-confirm" onClick={reset} disabled={busy} className="flex-1 py-2 border border-[#8A0303] text-[#E33333] font-heading text-xs tracking-widest uppercase">Confirm</button>
          <button onClick={() => setConfirm(false)} className="flex-1 py-2 border border-[#333] text-[#8c8c8c] font-heading text-xs tracking-widest uppercase">Cancel</button>
        </div>
      )}
    </div>
  );
}


export default function WorkshopView({ ch, setCh }) {
  return (
    <div className="space-y-2">
      <SetPreview ch={ch} />
      <ForgeCard ch={ch} setCh={setCh} />
      <EnchantCard ch={ch} setCh={setCh} />
      <FusionCard ch={ch} setCh={setCh} />
      <DismantleCard ch={ch} setCh={setCh} />
      <ScrollCard ch={ch} setCh={setCh} />
    </div>
  );
}
